import DOMPurify from 'dompurify';
import { supabase } from '../supabaseClient';

/**
 * Sanitiza strings para prevenção de stored/reflected XSS
 */
const sanitize = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();
};

/**
 * Registra um novo relatório de erro ou bug no sistema
 * @param {Object} reportData
 * @param {string} reportData.error_type - Categoria ('bug_visual', 'performance', 'mapa', 'dados_incorretos', 'outro')
 * @param {string} reportData.title - Título resumido
 * @param {string} reportData.description - Detalhamento do erro
 * @param {string} [reportData.user_name] - Nome opcional do usuário
 * @param {string} [reportData.contact] - Contato opcional (e-mail/whatsapp)
 * @param {string} [reportData.user_agent] - Informações do navegador/dispositivo
 * @param {string} [reportData.page_url] - URL onde o erro ocorreu
 */
export async function createErrorReport(reportData) {
  try {
    const payload = {
      error_type: sanitize(reportData.error_type) || 'outro',
      title: sanitize(reportData.title),
      description: sanitize(reportData.description),
      user_name: sanitize(reportData.user_name) || null,
      contact: sanitize(reportData.contact) || null,
      user_agent: sanitize(reportData.user_agent) || (typeof navigator !== 'undefined' ? navigator.userAgent : null),
      page_url: sanitize(reportData.page_url) || (typeof window !== 'undefined' ? window.location.href : null),
      status: 'pendente'
    };

    if (!payload.title) {
      throw new Error('O título do erro é obrigatório.');
    }
    if (!payload.description) {
      throw new Error('A descrição detalhada do erro é obrigatória.');
    }

    const { data, error } = await supabase
      .from('error_reports')
      .insert([payload])
      .select();

    if (error) {
      console.error('Erro ao registrar relatório de erro no Supabase:', error);
      throw error;
    }

    return data;
  } catch (err) {
    console.error('Exceção ao criar relatório de erro:', err);
    throw err;
  }
}

/**
 * Busca a lista de relatórios de erros registrados
 * @param {Object} [filters]
 * @param {string} [filters.status] - Filtro de status ('all', 'pendente', 'em_analise', 'resolvido')
 * @param {string} [filters.errorType] - Filtro de categoria de erro
 */
export async function getErrorReports(filters = {}) {
  try {
    let query = supabase
      .from('error_reports')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }
    if (filters.errorType && filters.errorType !== 'all') {
      query = query.eq('error_type', filters.errorType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao buscar relatórios de erros:', error);
      throw error;
    }

    return data || [];
  } catch (err) {
    console.error('Exceção ao carregar relatórios de erros:', err);
    throw err;
  }
}

/**
 * Atualiza o status de um relatório de erro
 * @param {string} id - UUID do erro
 * @param {'pendente' | 'em_analise' | 'resolvido'} newStatus - Novo status
 */
export async function updateErrorReportStatus(id, newStatus) {
  try {
    const validStatuses = ['pendente', 'em_analise', 'resolvido'];
    if (!validStatuses.includes(newStatus)) {
      throw new Error(`Status inválido: ${newStatus}. Permitidos: ${validStatuses.join(', ')}`);
    }

    const { data, error } = await supabase
      .from('error_reports')
      .update({ status: newStatus })
      .eq('id', id)
      .select();

    if (error) {
      console.error(`Erro ao atualizar status do erro ${id}:`, error);
      throw error;
    }

    return data;
  } catch (err) {
    console.error('Exceção ao atualizar status do relatório de erro:', err);
    throw err;
  }
}

/**
 * Deleta um relatório de erro pelo ID
 * @param {string} id - UUID do relatório a ser removido
 */
export async function deleteErrorReport(id) {
  try {
    const { data, error } = await supabase
      .from('error_reports')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Erro ao deletar relatório de erro ${id}:`, error);
      throw error;
    }

    return data;
  } catch (err) {
    console.error('Exceção ao deletar relatório de erro:', err);
    throw err;
  }
}
